import {
	Tile,
	Text,
	Label,
	Datepicker,
	Spacer,
	Button,
	Caption,
	InfoChip,
	H4,
	Timepicker,
	Select,
	NeutralChip,
	WarnChip,
	SuccessChip,
	ErrorChip,
} from '@components';
import styles from '../MobileETAUpdate.module.scss';
import {
	BUTTON_ICON_POS, BUTTON_TYPES, CHIP_SIZE, PartialInputEvent, SPACER_DIRECTION, TEOSLoadMasterManifest, TETAReason, TIMESLOT_STATUS, YourLoadItem
} from '@types';
import { ChevronRight, NorthEast, SouthEast } from '@mui/icons-material';
import {
	DATE_FORMATS, parseAndFormatDate, formatTimeFromString, formatDate, getTime
} from '@utils';
import { useEffect, useState } from 'react';
import { usePostRequestETAArrival } from '@api';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import isToday from 'date-fns/isToday';

type TETAStopProps = {
	load: YourLoadItem;
	stop: TEOSLoadMasterManifest['stops'][string];
	index: number;
	ETAReasons: TETAReason[];
	onTimeUpdate: () => void;
}

const ETAStop = (props: TETAStopProps) => {
	const {
		load,
		stop,
		index,
		ETAReasons,
		onTimeUpdate,
	} = props;
	const [ETADate, setETADate] = useState<{
		date: Date | null;
		time: string | null;
		endTime: string | null;
	}>({
		date: null,
		time: null,
		endTime: null
	})
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { callGetRequestETAArrivalData } = usePostRequestETAArrival();
	const [currentCargo, setCurrentCargo] = useState<TEOSLoadMasterManifest['stops'][string]['manifests'][number]['shipment']['cargos'][number] | null>(null);
	const [selectedETAReason, setSelectedETAReason] = useState<string | null>(null)
	const [serverErrors, setServerErrors] = useState<Record<string, string[]>>({});
	const updateDateAndTime = (e: PartialInputEvent, isTime = false) => {
		const { value } = e.target;
		let realValue = value;
		let endTimeValue = value;
		if (isTime) {
			if (typeof value !== 'string') {
				const tmpDate = getTime(value, true, false, true) as string;
				const futureDate = new Date(value);
				futureDate.setMinutes(futureDate.getMinutes() + 5);
				const futureTmpDate = getTime(futureDate, true, false, true) as string;
				realValue = tmpDate;
				endTimeValue = futureTmpDate;
			}
			setETADate((prev) => {
				return {
					...prev,
					time: realValue as string,
					endTime: endTimeValue as string
				}
			})
			return
		}
		setETADate((prev) => {
			return {
				...prev,
				date: realValue as Date
			}
		})
	}

	const handleOnETAReasonChange = (key: string) => {
		setSelectedETAReason(key)
	}
	const handleSaveTimeChange = () => {
		setIsLoading(true);
		setServerErrors({});
		const payload = {
			cargo_id: load.shipment.cargos[0].id,
			user_type: 'carrier',
			arrival_type: stop.type === 'pickup' ? 'Pickup' : 'DropOff',
			request_arrival_date: formatDate(ETADate.date as Date, DATE_FORMATS.API_DATE_FORMAT),
			request_arrival_start_time: ETADate.time,
			request_arrival_end_time: ETADate.endTime,
			reason: selectedETAReason,
		}
		if (!ETADate.date || !ETADate.time || !selectedETAReason?.length) return;
		callGetRequestETAArrivalData({ payload })
			.then((res) => {
				if (res?.data) {
					onTimeUpdate();
					setIsOpen(false);
				}
			})
			.catch((err) => {
				console.log({ err })
				setServerErrors(err.cause.response.data.errors || [])
			})
			.finally(() => {
				setIsLoading(false);
			})
	}

	const getTimeSlotStatusChip = () => {
		if (!currentCargo || currentCargo[stop.type].time_slot_status === TIMESLOT_STATUS.NOT_REQUIRED) {
			return null
		}
		return <>
			<Spacer size={8} />
			<NeutralChip text={'Mandatory time-slot'} CustomIcon={AccessTimeOutlinedIcon} size={CHIP_SIZE.MEDIUM} />
		</>
	}

	const getValidETA = () => {
		if (stop.type === 'pickup') {
			return [
				stop.approved_carrier_pick_up_estimation_time_of_arrival,
				stop.pending_carrier_pick_up_estimation_time_of_arrival,
				stop.rejected_carrier_pick_up_estimation_time_of_arrival,
			].find((eta) => Boolean(eta) === true);
		}
		if (stop.type === 'dropoff') {
			return [
				stop.approved_carrier_drop_off_estimation_time_of_arrival,
				stop.pending_carrier_drop_off_estimation_time_of_arrival,
				stop.rejected_carrier_drop_off_estimation_time_of_arrival,
			].find((eta) => Boolean(eta) === true);
		}
		return null
	}

	const getExistingETAChip = () => {
		if (stop.type === 'pickup') {
			if (stop.pending_carrier_pick_up_estimation_time_of_arrival) {
				return <WarnChip size={CHIP_SIZE.SMALL} text={'Pending Review'} CustomIcon={PendingOutlinedIcon} />
			}
			if (stop.approved_carrier_pick_up_estimation_time_of_arrival) {
				return <SuccessChip size={CHIP_SIZE.SMALL} text={'Approved'} CustomIcon={CheckCircleOutlinedIcon} />
			}
			if (stop.rejected_carrier_pick_up_estimation_time_of_arrival) {
				return <ErrorChip size={CHIP_SIZE.SMALL} text={'Rejected'} CustomIcon={CancelOutlinedIcon} />
			}
		}
		if (stop.type === 'dropoff') {
			if (stop.pending_carrier_drop_off_estimation_time_of_arrival) {
				return <WarnChip size={CHIP_SIZE.SMALL} text={'Pending Review'} CustomIcon={PendingOutlinedIcon} />
			}
			if (stop.approved_carrier_drop_off_estimation_time_of_arrival) {
				return <SuccessChip size={CHIP_SIZE.SMALL} text={'Approved'} CustomIcon={CheckCircleOutlinedIcon} />
			}
			if (stop.rejected_carrier_drop_off_estimation_time_of_arrival) {
				return <ErrorChip size={CHIP_SIZE.SMALL} text={'Rejected'} CustomIcon={CancelOutlinedIcon} />
			}
		}
	}

	const getETAForm = () => {
		return <>
			<Spacer size={20} />
			<Datepicker
				label='Estimated time of arrival'
				onChange={(e) => updateDateAndTime(e as PartialInputEvent)}
				value={ETADate.date ?? ''}
				name={'request_arrival_date'}
				keepErrorSpace={false}
				placeholder={'dd/mm/yyyy'}
				disablePast
				readOnly
				hasError={!!serverErrors?.request_arrival_date}
				errorMessage={serverErrors?.request_arrival_date?.[0]}
			/>
			<Spacer size={8} />
			<Timepicker
				label=''
				onChange={(e) => updateDateAndTime(e as PartialInputEvent, true)}
				value={ETADate.time ?? ''}
				name={'request_arrival_time'}
				keepErrorSpace={false}
				placeholder={'hh:mm'}
				hasError={!!serverErrors?.request_arrival_time}
				errorMessage={serverErrors?.request_arrival_time?.[0]}
				readOnly
			/>
			<Spacer size={16} />
			<Select
				onChange={handleOnETAReasonChange}
				value={selectedETAReason}
				name={'eta-reason'}
				label='Reason for update'
				hasError={!!serverErrors?.reason}
				errorMessage={serverErrors?.reason?.[0]}
			>
				{ETAReasons.map((reason, index) => {
					return (
						<Select.Option
							key={index}
							value={reason.key}
						>
							{reason.name}
						</Select.Option>
					)
				})}
			</Select>
			<Spacer size={20} />
			<div className={styles.buttonContainer}>
				<Button
					text={'Cancel'}
					type={BUTTON_TYPES.TERTIARY}
					onClick={() => setIsOpen(false)}
				/>
				<Button
					text={'Update ETA'}
					disabled={!ETADate.date || !ETADate.time || !selectedETAReason}
					loading={isLoading}
					onClick={handleSaveTimeChange}
				/>
			</div>
		</>
	}

	useEffect(() => {
		if (stop?.manifests?.[0]?.shipment?.cargos[0]) {
			setCurrentCargo(stop?.manifests?.[0]?.shipment?.cargos[0]);
		}
	}, [stop?.manifests?.[0]?.shipment?.cargos[0]]);

	return <Tile stretch>
		<div className={styles.updateETAContent}>
			<Caption>
				Stop {index}
			</Caption>
			<div className={styles.updateETAChip}>
				<InfoChip
					CustomIcon={stop.type === 'pickup' ? NorthEast : SouthEast}
					text={stop.type === 'pickup' ? 'Pick-up' : 'Drop-off'}
					size={CHIP_SIZE.SMALL}
				/>
			</div>
		</div>
		<H4>
			{stop.shipment_address.business_name}
		</H4>
		<Text>
			{stop.shipment_address.address}
		</Text>
		{
			currentCargo &&
			<div className={styles.detailsRow}>
				<Text>
					{isToday(new Date((stop.type === 'pickup' ?
						currentCargo.pickup_at :
						currentCargo.drop_at
					))) ? 'Today, ' : ''}
					{parseAndFormatDate(
						(stop.type === 'pickup' ?
							currentCargo.pickup_at :
							currentCargo.drop_at
						),
						DATE_FORMATS.DATE_WITH_FULL_MONTH_NAME
					)}
				</Text>
				<Spacer
					size={16}
					divider
					dir={SPACER_DIRECTION.VERTICAL}
					dividerColor={'var(--neutral-03)'}
				/>
				<Text>
					{formatTimeFromString(stop.type === 'pickup' ?
						currentCargo?.pick_from :
						currentCargo?.drop_from
					, false)}{' '}
					- {formatTimeFromString(stop.type === 'pickup' ?
						currentCargo?.pick_to :
						currentCargo?.drop_to
					, false)}
				</Text>

			</div>
		}
		{
			getTimeSlotStatusChip()
		}
		{
			getValidETA() && <>
				<Spacer size={20} />
				<Tile stretch padding={12}>
					{getExistingETAChip()}
					<Spacer size={12} />
					<Label>
						Your estimated time of arrival
					</Label>
					<Text>
						{parseAndFormatDate((getValidETA()?.request_arrival_date as string),
							DATE_FORMATS.DATE_WITH_FULL_MONTH_NAME
						)}{' '}
						- {formatTimeFromString((getValidETA()?.request_arrival_start_time as string))}{' '}
						- {formatTimeFromString((getValidETA()?.request_arrival_end_time as string))}
					</Text>
					<Text>
						{
							getValidETA()?.reason
						}
					</Text>
					{
						getValidETA()?.note &&
						<Text>
							{getValidETA()?.note}
						</Text>
					}
					{
						!isOpen && load.master_manifest.super_dock_id === null && load.status.short !== 'Allocated' && <>
							<Spacer size={20} />
							<Button
								type={BUTTON_TYPES.TERTIARY}
								onClick={() => setIsOpen(true)}
								text={'Update your ETA'}
								Icon={ChevronRight}
								iconPos={BUTTON_ICON_POS.RIGHT}
							/>
						</>
					}
					{
						isOpen && getETAForm()
					}
				</Tile>
			</>
		}
		{
			!getValidETA() && !isOpen && load.master_manifest.super_dock_id === null && load.status.short !== 'Allocated' && <>
				<Spacer size={20} />
				<Button
					type={BUTTON_TYPES.TERTIARY}
					onClick={() => setIsOpen(true)}
					text={'Update your ETA'}
					Icon={ChevronRight}
					iconPos={BUTTON_ICON_POS.RIGHT}
				/>
			</>
		}
		{
			isOpen && !getValidETA() && getETAForm()
		}
	</Tile>
}

export default ETAStop;
